import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  ClipboardCheck, Plus, Loader2, FileDown, Trash2, CheckCircle2, XCircle, MinusCircle, Printer,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dates";

const STATUS_OPTIONS = [
  { id: "yes", label: "Yes", color: "text-emerald-700 bg-emerald-50 border-emerald-300", Icon: CheckCircle2 },
  { id: "no",  label: "No",  color: "text-red-700 bg-red-50 border-red-300",             Icon: XCircle },
  { id: "na",  label: "N/A", color: "text-stone-700 bg-stone-50 border-stone-300",       Icon: MinusCircle },
];

const QUARTERS = [
  { id: "q1", label: "Q1 (Jan–Mar)" },
  { id: "q2", label: "Q2 (Apr–Jun)" },
  { id: "q3", label: "Q3 (Jul–Sep)" },
  { id: "q4", label: "Q4 (Oct–Dec)" },
];

export default function PlaygroundInspections() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [checklist, setChecklist] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/playground-inspections")
      .then((r) => setList(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/playground-inspections/checklist").then((r) => setChecklist(r.data));
  }, []);

  const openPdf = async (id) => {
    try {
      const res = await api.get(`/playground-inspections/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeInspection = async (id) => {
    if (!window.confirm("Delete this inspection?")) return;
    try { await api.delete(`/playground-inspections/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const printBlankPdf = async () => {
    try {
      const res = await api.get(`/playground-inspections/blank/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="playground-page" className="space-y-8">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-700">Compliance · DCY 01281 (Rev. 8/2025)</div>
          <h1 className="mt-2 font-display text-4xl font-medium text-stone-900">Playground inspections</h1>
          <p className="mt-2 text-stone-500">Ohio Department of Children and Youth requires a quarterly playground inspection. Fill once, print the DCY 01281, and keep on file for one year.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Button
            data-testid="pg-print-blank-btn"
            variant="outline"
            onClick={printBlankPdf}
            className="rounded-xl h-11"
            title="Open a blank DCY 01281 you can print, walk to the playground, and fill in by hand."
          >
            <Printer className="w-4 h-4 mr-1" /> Print blank form
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="pg-new-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
                <Plus className="w-4 h-4 mr-1" /> New inspection
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>DCY 01281 (Rev. 8/2025) — Playground Inspection</DialogTitle>
                <DialogDescription>All 22 Ohio items. The generated PDF is print-ready and mirrors the state form.</DialogDescription>
              </DialogHeader>
              <InspectionForm checklist={checklist} onSaved={() => { setOpen(false); load(); }} />
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {loading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-700" /></div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-14 text-center">
          <ClipboardCheck className="w-8 h-8 mx-auto text-stone-300" />
          <p className="mt-3 font-display text-xl text-stone-900">No inspections on file.</p>
          <p className="mt-1 text-sm text-stone-500">Record your first quarterly inspection — takes about 5 minutes.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="pg-list">
          {list.map((ins) => {
            const items = ins.items || {};
            const failed = Object.values(items).filter((v) => (v?.status || "").toLowerCase() === "no").length;
            const passed = Object.values(items).filter((v) => (v?.status || "").toLowerCase() === "yes").length;
            const naCount = Object.values(items).filter((v) => (v?.status || "").toLowerCase() === "na").length;
            const qLabel = QUARTERS.find((q) => q.id === ins.playground_1_quarter)?.label || "—";
            return (
              <article key={ins.id} data-testid={`pg-card-${ins.id}`} className="rounded-2xl border border-stone-200 bg-white p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">{fmtDate(ins.inspection_date)} · {qLabel}</div>
                    <h3 className="mt-1 font-display text-lg font-medium text-stone-900">{ins.inspector_name}</h3>
                    {ins.inspector_role && <div className="text-xs text-stone-500">{ins.inspector_role}</div>}
                  </div>
                  <button onClick={() => removeInspection(ins.id)} data-testid={`pg-delete-${ins.id}`} className="text-stone-400 hover:text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4 flex items-center gap-4 text-xs">
                  <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> {passed} yes</span>
                  <span className="inline-flex items-center gap-1 text-red-700"><XCircle className="w-3 h-3" /> {failed} no</span>
                  <span className="inline-flex items-center gap-1 text-stone-500"><MinusCircle className="w-3 h-3" /> {naCount} N/A</span>
                </div>
                {ins.corrective_actions && (
                  <div className="mt-3 text-xs text-stone-700 bg-orange-50 border border-orange-200 rounded-lg p-2">
                    <strong>Plan of action:</strong> {ins.corrective_actions}
                  </div>
                )}
                <Button data-testid={`pg-pdf-${ins.id}`} onClick={() => openPdf(ins.id)} variant="outline" className="mt-4 rounded-xl">
                  <Printer className="w-4 h-4 mr-1" /> Print DCY 01281
                </Button>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InspectionForm({ checklist, onSaved }) {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    inspection_date: today,
    inspector_name: user?.name || "",
    inspector_role: "",
    inspector_signature: user?.name || "",
    administrator_signature: "",
    playground_1_quarter: "",
    playground_1_location: "on_site",
    playground_2_enabled: false,
    playground_2_quarter: "",
    playground_2_location: "on_site",
    items: {},
    overall_notes: "",
    corrective_actions: "",
  });
  const [busy, setBusy] = useState(false);
  const upItem = (key, patch) => setForm((f) => ({ ...f, items: { ...f.items, [key]: { ...(f.items[key] || {}), ...patch } } }));
  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.inspector_name.trim()) {
      return toast.error("Inspector name required.");
    }
    if (!form.playground_1_quarter) {
      return toast.error("Select the quarter for Playground 1.");
    }
    setBusy(true);
    try { await api.post("/playground-inspections", form); toast.success("Inspection saved."); if (typeof onSaved === "function") onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="pg-form" className="space-y-5 pt-2">
      {/* Inspector + date */}
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Date</Label>
          <Input data-testid="pg-date" type="date" value={form.inspection_date} onChange={(e) => up("inspection_date", e.target.value)} required className="mt-2 h-11" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Inspector name</Label>
          <Input data-testid="pg-inspector-name" value={form.inspector_name} onChange={(e) => up("inspector_name", e.target.value)} required className="mt-2 h-11" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500">Role (optional)</Label>
          <Input data-testid="pg-inspector-role" value={form.inspector_role} onChange={(e) => up("inspector_role", e.target.value)} placeholder="Administrator, Director…" className="mt-2 h-11" />
        </div>
      </div>

      {/* Playground 1 header */}
      <PlaygroundHeader
        title="Playground 1"
        quarter={form.playground_1_quarter}
        setQuarter={(v) => up("playground_1_quarter", v)}
        location={form.playground_1_location}
        setLocation={(v) => up("playground_1_location", v)}
        testIdPrefix="pg1"
      />

      {/* Optional Playground 2 */}
      <div className="flex items-center gap-2">
        <Checkbox
          data-testid="pg-enable-p2"
          id="pg-enable-p2"
          checked={form.playground_2_enabled}
          onCheckedChange={(v) => up("playground_2_enabled", !!v)}
        />
        <label htmlFor="pg-enable-p2" className="text-sm text-stone-700">
          We have a second playground on site
        </label>
      </div>
      {form.playground_2_enabled && (
        <PlaygroundHeader
          title="Playground 2"
          quarter={form.playground_2_quarter}
          setQuarter={(v) => up("playground_2_quarter", v)}
          location={form.playground_2_location}
          setLocation={(v) => up("playground_2_location", v)}
          testIdPrefix="pg2"
        />
      )}

      {/* 22-item list */}
      <div>
        <div className="text-xs uppercase tracking-widest font-semibold text-stone-500 mb-2">
          Area being assessed — any item marked "No" must be corrected.
        </div>
        <div className="rounded-xl border border-stone-200 divide-y divide-stone-100">
          {checklist.map((c, idx) => {
            const entry = form.items[c.key] || {};
            return (
              <div key={c.key} data-testid={`pg-item-${c.key}`} className="grid md:grid-cols-12 gap-3 p-4 items-start">
                <div className="md:col-span-5 text-sm text-stone-800">
                  <span className="font-semibold text-emerald-800 mr-2">{String(idx + 1).padStart(2, "0")}.</span>
                  {c.label}
                </div>

                <div className="md:col-span-3">
                  <Label className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">Playground 1</Label>
                  <RadioGroup value={entry.status || ""} onValueChange={(v) => upItem(c.key, { status: v })} className="flex gap-2">
                    {STATUS_OPTIONS.map((o) => (
                      <label key={o.id} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg border text-xs font-semibold cursor-pointer ${entry.status === o.id ? o.color : "text-stone-500 border-stone-200 bg-white"}`}>
                        <RadioGroupItem value={o.id} data-testid={`pg-status-${c.key}-${o.id}`} className="sr-only" />
                        <o.Icon className="w-3.5 h-3.5" /> {o.label}
                      </label>
                    ))}
                  </RadioGroup>
                  {entry.status === "no" && (
                    <Input
                      data-testid={`pg-datecorrected-${c.key}`}
                      type="date"
                      placeholder="Date corrected"
                      value={entry.date_corrected || ""}
                      onChange={(e) => upItem(c.key, { date_corrected: e.target.value })}
                      className="mt-2 h-8 text-xs"
                    />
                  )}
                </div>

                {form.playground_2_enabled && (
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-widest text-stone-500 block mb-1">Playground 2</Label>
                    <RadioGroup value={entry.status2 || ""} onValueChange={(v) => upItem(c.key, { status2: v })} className="flex gap-2">
                      {STATUS_OPTIONS.map((o) => (
                        <label key={o.id} className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-semibold cursor-pointer ${entry.status2 === o.id ? o.color : "text-stone-500 border-stone-200 bg-white"}`}>
                          <RadioGroupItem value={o.id} data-testid={`pg-status2-${c.key}-${o.id}`} className="sr-only" />
                          <o.Icon className="w-3 h-3" />
                        </label>
                      ))}
                    </RadioGroup>
                    {entry.status2 === "no" && (
                      <Input
                        data-testid={`pg-datecorrected2-${c.key}`}
                        type="date"
                        value={entry.date_corrected2 || ""}
                        onChange={(e) => upItem(c.key, { date_corrected2: e.target.value })}
                        className="mt-2 h-8 text-xs"
                      />
                    )}
                  </div>
                )}

                <Input
                  data-testid={`pg-notes-${c.key}`}
                  placeholder="Notes (optional)"
                  value={entry.notes || ""}
                  onChange={(e) => upItem(c.key, { notes: e.target.value })}
                  className={`${form.playground_2_enabled ? "md:col-span-2" : "md:col-span-4"} h-9 text-sm`}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500">Comments / Plan of Action</Label>
        <Textarea data-testid="pg-corrective-actions" value={form.corrective_actions} onChange={(e) => up("corrective_actions", e.target.value)} rows={3} className="mt-2" placeholder="Describe follow-up actions, deadlines, or vendor requests…" />
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-xl bg-stone-50 border border-stone-200 p-4">
          <Label className="text-xs uppercase tracking-widest font-bold text-emerald-700">Inspector signature</Label>
          <Input
            data-testid="pg-signature"
            value={form.inspector_signature}
            onChange={(e) => up("inspector_signature", e.target.value)}
            required className="mt-2 h-11" placeholder="Type full name to sign"
          />
        </div>
        <div className="rounded-xl bg-stone-50 border border-stone-200 p-4">
          <Label className="text-xs uppercase tracking-widest font-bold text-emerald-700">Administrator / Provider signature</Label>
          <Input
            data-testid="pg-admin-signature"
            value={form.administrator_signature}
            onChange={(e) => up("administrator_signature", e.target.value)}
            className="mt-2 h-11" placeholder="Optional — required if different than inspector"
          />
        </div>
      </div>

      <Button type="submit" data-testid="pg-submit" disabled={busy} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl h-11">
        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save & generate printable DCY 01281"}
      </Button>
    </form>
  );
}

function PlaygroundHeader({ title, quarter, setQuarter, location, setLocation, testIdPrefix }) {
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-800">{title}</div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 block mb-2">Quarter of inspection</Label>
          <div className="flex flex-wrap gap-2">
            {QUARTERS.map((q) => (
              <label key={q.id} className={`cursor-pointer text-xs px-3 py-1.5 rounded-lg border ${quarter === q.id ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-stone-200"}`}>
                <input
                  type="radio"
                  name={`${testIdPrefix}-quarter`}
                  data-testid={`${testIdPrefix}-quarter-${q.id}`}
                  checked={quarter === q.id}
                  onChange={() => setQuarter(q.id)}
                  className="sr-only"
                />
                {q.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500 block mb-2">Location</Label>
          <div className="flex gap-2">
            {[{ id: "on_site", label: "On-site" }, { id: "off_site", label: "Off-site" }].map((o) => (
              <label key={o.id} className={`cursor-pointer text-xs px-3 py-1.5 rounded-lg border ${location === o.id ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-stone-200"}`}>
                <input
                  type="radio"
                  name={`${testIdPrefix}-location`}
                  data-testid={`${testIdPrefix}-location-${o.id}`}
                  checked={location === o.id}
                  onChange={() => setLocation(o.id)}
                  className="sr-only"
                />
                {o.label}
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
